/**
 * idbHydration — pre-fills Redux from IndexedDB before any API call fires.
 *
 * Dispatches are tagged fromCache:true so the idbSyncMiddleware skips
 * writing them back to IDB, avoiding a pointless round-trip.
 *
 * Usage:
 *   await hydrateFromIDB();          // in login() and serverReconnect()
 *   const t = await getLastSyncTime('recentChats');  // for delta-sync cursor
 */

import uuidv4 from 'uuid/v4';
import { RECENT_CHAT_DATA, GROUPS_DATA, UNREAD_USER_OBJ, VCARD_DATA } from '../Actions/Constants';
import { Store } from '../store';
import logger from '../Helpers/logger';
import IndexedDb from './IndexedDb';
import { formatUserIdToJid } from './Chat/User';

const idb = new IndexedDb();

/**
 * Load recent chats and groups from IDB into Redux.
 * Called at the very start of login() and serverReconnect() so the UI
 * renders immediately from cache while API calls proceed in the background.
 */
export const hydrateFromIDB = async () => {
    try {
        const [recentChats, groups, ownDisappearMeta] = await Promise.all([
            idb.getRecentChats(),
            idb.getGroups(),
            idb.getMetadata('own_msgDisappear'),
        ]);

        if (Array.isArray(recentChats) && recentChats.length) {
            Store.dispatch({
                type: RECENT_CHAT_DATA,
                payload: { id: uuidv4(), data: recentChats, fromCache: true },
            });

            // Restore unread badges immediately from IDB so the UI doesn't
            // show 0 while waiting for serverReconnect to run the API loop.
            // Tagged fromCache:true so the middleware skips writing back to IDB.
            for (const chat of recentChats) {
                const count = parseInt(chat.unreadCount) || 0;
                if (count > 0) {
                    Store.dispatch({
                        type: UNREAD_USER_OBJ,
                        payload: {
                            id: uuidv4(),
                            fromCache: true,
                            data: {
                                count,
                                fromUserId: chat.fromUserId,
                                chatJid: formatUserIdToJid(chat.fromUserId, chat.chatType),
                                fullyViewedChat: false,
                                fetchLastMsgId: chat.unreadMessageId || '',
                            },
                        },
                    });
                }
            }
        }

        if (Array.isArray(groups) && groups.length) {
            Store.dispatch({
                type: GROUPS_DATA,
                payload: { id: uuidv4(), data: groups, fromCache: true },
            });
        }

        // Restore own account-level disappear setting from IDB so it's available in
        // vCardData BEFORE getUserProfile fires and potentially overwrites it without
        // the field. Tagged fromCache:true so idbSyncMiddleware skips writing it back.
        const storedMsgDisappear = ownDisappearMeta?.value;
        if (storedMsgDisappear !== undefined && storedMsgDisappear !== null) {
            const existingVCard = Store.getState().vCardData?.data || {};
            Store.dispatch({
                type: VCARD_DATA,
                payload: {
                    id: uuidv4(),
                    fromCache: true,
                    data: { ...existingVCard, msgDisappear: Number(storedMsgDisappear) },
                },
            });
            // Mirror to localStorage so userProfileListener fallback also finds it.
            try {
                localStorage.setItem('_daz_own_msg_disappear', String(storedMsgDisappear));
            } catch (_) {}
        }
    } catch (e) {
        // IDB unavailable (e.g. private browsing) — silent fallback to API-only
        logger.warn('[IDB] hydrateFromIDB failed', e);
    }
};

/**
 * Returns the unix timestamp (ms) when a store was last synced from the API,
 * or 0 if the store has never been synced. Used as the messageTime cursor for
 * delta sync in getRecentChatsByPagination.
 */
export const getLastSyncTime = async storeName => {
    try {
        const meta = await idb.getMetadata(storeName);
        return meta?.lastSyncAt || 0;
    } catch (e) {
        return 0;
    }
};
